import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faListUl, faSync, faPlus, faTrash,
    faChevronUp, faChevronDown, faLayerGroup, faPuzzlePiece,
    faShieldAlt, faCheckSquare, faSquare
} from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

/**
 * MenuComposer — Assign modules to groups (tf_menu_item).
 * Left column: group selector.
 * Right column: modules in the selected group + available modules to add.
 * Now includes "Default Permissions" to auto-grant access to profiles.
 */
const MenuComposer = () => {
    const [groups,     setGroups]     = useState([]);
    const [modules,    setModules]    = useState([]);
    const [items,      setItems]      = useState([]);   // tf_menu_item rows
    const [profiles,   setProfiles]   = useState([]);
    const [selGroup,   setSelGroup]   = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [saving,     setSaving]     = useState(false);
    
    // Default profiles to grant access when adding a module
    const [grantProfiles, setGrantProfiles] = useState([]);

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/groups',     {}, r => { if (r.success) setGroups(r.data || []); });
        request.Get(API_BASE + 'superadmin/modules',    {}, r => { if (r.success) setModules(r.data || []); });
        request.Get(API_BASE + 'superadmin/profiles',   {}, r => { 
            if (r.success) {
                setProfiles(r.data || []);
                // By default, suggest granting to all active profiles
                setGrantProfiles((r.data || []).map(p => p.ProfileId));
            }
        });
        request.Get(API_BASE + 'superadmin/menu-items', {}, r => {
            if (r.success) setItems(r.data || []);
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    // Items for the selected group, sorted by ItemOrder
    const groupItems = items
        .filter(i => i.GroupId === selGroup?.GroupId)
        .sort((a, b) => a.ItemOrder - b.ItemOrder);

    // Modules NOT yet assigned to the selected group
    const assignedModuleIds = new Set(groupItems.map(i => i.ModuleId));
    const availableModules  = modules.filter(m => !assignedModuleIds.has(m.ModuleId) && m.ModuleActive);

    const handleAddModule = (mod) => {
        if (!selGroup) return;
        setSaving(true);
        const maxOrder = groupItems.length > 0 ? Math.max(...groupItems.map(i => i.ItemOrder)) + 1 : 0;
        
        // 1. Create the menu item
        request.Post(API_BASE + 'superadmin/menu-items', {
            GroupId: selGroup.GroupId, ModuleId: mod.ModuleId, ItemOrder: maxOrder, ItemActive: 1
        }, r => {
            if (r.success) {
                const newItem = r.data;
                Notify({ type: 'success', text: `"${mod.ModuleLabel}" agregado al grupo` });
                setItems(prev => [...prev, newItem]);

                // 2. Automatically grant "View" permission to selected profiles
                if (grantProfiles.length > 0) {
                    grantProfiles.forEach(pid => {
                        request.Post(API_BASE + 'superadmin/profile-permissions', {
                            profileId: pid,
                            permissions: [{
                                ModuleId: mod.ModuleId,
                                ItemId: newItem.ItemId,
                                CanView: 1, CanEdit: 0, CanDelete: 0
                            }]
                        }, () => {});
                    });
                }
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al agregar' });
            }
            setSaving(false);
        }, 'json', () => setSaving(false));
    };

    const handleRemoveItem = (item) => {
        setSaving(true);
        request.Delete(API_BASE + `superadmin/menu-items/${item.ItemId}`, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Ítem removido del grupo' });
                setItems(prev => prev.filter(i => i.ItemId !== item.ItemId));
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al remover' });
            }
            setSaving(false);
        }, 'json', () => setSaving(false));
    };

    const handleReorder = (item, direction) => {
        // Find neighbours
        const list  = [...groupItems];
        const idx   = list.findIndex(i => i.ItemId === item.ItemId);
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= list.length) return;

        // Swap orders optimistically
        const newList = [...list];
        const tmpOrder = newList[idx].ItemOrder;
        newList[idx].ItemOrder    = newList[swapIdx].ItemOrder;
        newList[swapIdx].ItemOrder = tmpOrder;

        setItems(prev => prev.map(i => {
            const found = newList.find(n => n.ItemId === i.ItemId);
            return found ? { ...i, ItemOrder: found.ItemOrder } : i;
        }));

        // Persist both
        request.Put(API_BASE + `superadmin/menu-items/${newList[idx].ItemId}`,    { ItemOrder: newList[idx].ItemOrder },    () => {});
        request.Put(API_BASE + `superadmin/menu-items/${newList[swapIdx].ItemId}`, { ItemOrder: newList[swapIdx].ItemOrder }, () => {});
    };

    const toggleProfile = (id) => {
        setGrantProfiles(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    return (
        <div>
            <div className="d-flex justify-content-end mb-4">
                <button className="sa-btn sa-btn-ghost" onClick={fetchAll} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> Recargar
                </button>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
            ) : (
                <div className="row g-4">
                    {/* Left: Group selector */}
                    <div className="col-md-4">
                        <div className="sa-panel">
                            <div className="sa-panel-header">
                                <h6 className="sa-panel-title">
                                    <FontAwesomeIcon icon={faLayerGroup} style={{ color: 'var(--sa-primary)' }} />
                                    Grupos de Menú
                                </h6>
                            </div>
                            <div className="sa-panel-body p-0">
                                {groups.length === 0 ? (
                                    <p className="text-muted p-3 small">Sin grupos. Creá uno primero en la pestaña Grupos.</p>
                                ) : (
                                    <div>
                                        {groups.map(g => (
                                            <div
                                                key={g.GroupId}
                                                onClick={() => setSelGroup(g)}
                                                style={{
                                                    padding: '0.9rem 1.25rem',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                                                    background: selGroup?.GroupId === g.GroupId ? 'rgba(168,85,247,0.12)' : 'transparent',
                                                    borderLeft: `3px solid ${selGroup?.GroupId === g.GroupId ? 'var(--sa-primary)' : 'transparent'}`,
                                                    transition: 'all 0.15s',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                                }}
                                            >
                                                <span style={{ fontWeight: 600, color: selGroup?.GroupId === g.GroupId ? '#f8fafc' : '#94a3b8', fontSize: '0.88rem' }}>
                                                    {g.GroupLabel}
                                                </span>
                                                <span className="sa-badge sa-badge-premium" style={{ fontSize: '0.62rem' }}>
                                                    {items.filter(i => i.GroupId === g.GroupId).length}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: Items in selected group + available modules */}
                    <div className="col-md-8">
                        {!selGroup ? (
                            <div className="sa-empty" style={{ height: '100%' }}>
                                <FontAwesomeIcon icon={faListUl} style={{ fontSize: '2.5rem', opacity: 0.1, display: 'block', margin: '0 auto 1rem' }} />
                                <p style={{ color: '#475569' }}>Seleccioná un grupo de la izquierda para editar sus ítems</p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-4">
                                {/* Assigned items */}
                                <div className="sa-panel">
                                    <div className="sa-panel-header">
                                        <h6 className="sa-panel-title">
                                            <FontAwesomeIcon icon={faListUl} style={{ color: 'var(--sa-primary)' }} />
                                            Ítems asignados — <em style={{ color: '#64748b', fontStyle: 'normal' }}>{selGroup.GroupLabel}</em>
                                        </h6>
                                        <span className="sa-badge sa-badge-premium">{groupItems.length} módulos</span>
                                    </div>
                                    <div className="sa-panel-body p-0">
                                        {groupItems.length === 0 ? (
                                            <p className="text-muted p-3 small">Este grupo no tiene módulos asignados aún.</p>
                                        ) : (
                                            groupItems.map((item, idx) => {
                                                const mod = modules.find(m => m.ModuleId === item.ModuleId);
                                                return (
                                                    <div key={item.ItemId} style={{
                                                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                        padding: '0.9rem 1.25rem',
                                                        borderBottom: '1px solid rgba(255,255,255,0.04)'
                                                    }}>
                                                        <span style={{ color: '#475569', fontSize: '0.75rem', width: 20, textAlign: 'right' }}>
                                                            {idx + 1}
                                                        </span>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#f1f5f9' }}>
                                                                {mod?.ModuleLabel || '?'}
                                                            </div>
                                                            <div style={{ fontSize: '0.72rem', color: '#475569' }}>
                                                                {mod?.ModuleKey} · {mod?.ModuleType}
                                                            </div>
                                                        </div>
                                                        {/* Reorder */}
                                                        <div className="d-flex flex-column" style={{ gap: 2 }}>
                                                            <button
                                                                className="sa-btn sa-btn-ghost sa-btn-sm"
                                                                style={{ padding: '2px 6px' }}
                                                                onClick={() => handleReorder(item, 'up')}
                                                                disabled={idx === 0 || saving}
                                                            >
                                                                <FontAwesomeIcon icon={faChevronUp} />
                                                            </button>
                                                            <button
                                                                className="sa-btn sa-btn-ghost sa-btn-sm"
                                                                style={{ padding: '2px 6px' }}
                                                                onClick={() => handleReorder(item, 'down')}
                                                                disabled={idx === groupItems.length - 1 || saving}
                                                            >
                                                                <FontAwesomeIcon icon={faChevronDown} />
                                                            </button>
                                                        </div>
                                                        <button
                                                            className="sa-btn sa-btn-danger sa-btn-sm"
                                                            onClick={() => handleRemoveItem(item)}
                                                            disabled={saving}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Available modules to add */}
                                <div className="sa-panel">
                                    <div className="sa-panel-header d-flex justify-content-between align-items-center">
                                        <h6 className="sa-panel-title mb-0">
                                            <FontAwesomeIcon icon={faPuzzlePiece} style={{ color: '#10b981' }} />
                                            Módulos disponibles
                                        </h6>
                                        <div className="d-flex align-items-center gap-2 small text-muted">
                                            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '0.7rem' }} />
                                            Auto-habilitar en:
                                        </div>
                                    </div>
                                    
                                    {/* Quick Profile Grant Selector */}
                                    <div style={{ padding: '0.75rem 1.25rem', background: 'rgba(16,185,129,0.03)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div className="d-flex gap-3 flex-wrap">
                                            {profiles.map(p => (
                                                <div 
                                                    key={p.ProfileId} 
                                                    onClick={() => toggleProfile(p.ProfileId)}
                                                    style={{ 
                                                        cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '6px',
                                                        color: grantProfiles.includes(p.ProfileId) ? '#10b981' : '#475569',
                                                        fontWeight: grantProfiles.includes(p.ProfileId) ? 700 : 500,
                                                        transition: 'all 0.15s'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={grantProfiles.includes(p.ProfileId) ? faCheckSquare : faSquare} />
                                                    {p.ProfileName}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="sa-panel-body p-0">
                                        {availableModules.length === 0 ? (
                                            <p className="text-muted p-3 small">Todos los módulos activos ya están asignados a este grupo.</p>
                                        ) : (
                                            availableModules.map(mod => (
                                                <div key={mod.ModuleId} style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                    padding: '0.8rem 1.25rem',
                                                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                                                    opacity: saving ? 0.5 : 1
                                                }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#f1f5f9' }}>
                                                            {mod.ModuleLabel}
                                                        </div>
                                                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                                            {mod.ModuleKey} · {mod.ModuleType}
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="sa-btn sa-btn-success sa-btn-sm"
                                                        onClick={() => handleAddModule(mod)}
                                                        disabled={saving}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} /> Agregar
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuComposer;
